import { fetchWithKeycloakToken } from '@/features/Keycloak/keycloakService';
import { useEffect, useState } from 'react';

export function useFetchMedia({
  mediaEndpointUrl,
}: {
  mediaEndpointUrl?: string;
}) {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!mediaEndpointUrl) {
      setMediaUrl('');
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let isActive = true;

    const fetchMedia = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchWithKeycloakToken(mediaEndpointUrl, {
          signal: controller.signal,
        });

        if (!response.ok) throw new Error('Media fetch failed');

        let url = '';
        if (response.redirected) {
          url = response.url;
          response.body?.cancel();
        } else {
          const contentType = response.headers.get('content-type') ?? '';
          if (contentType.includes('application/json')) {
            const payload = (await response.json()) as { url?: string };
            url = payload.url ?? '';
          } else {
            url = await response.text();
          }
        }

        if (!isActive) {
          return;
        }
        setMediaUrl(url);
      } catch (t) {
        if (t instanceof Error && t.name === 'AbortError') {
          return;
        }
        if (!isActive) {
          return;
        }
        setError(t instanceof Error ? t : new Error('Unknown error'));
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    void fetchMedia();
    return () => {
      isActive = false;
      controller.abort();
    };
  }, [mediaEndpointUrl]);

  return { mediaUrl, loading, error };
}
